// ── 계약 등록 모달의 섹션·칸 이름표 ──────────────────────────────────────────
// 설정 화면(ContractFieldSettingsModal)과 계약 모달이 함께 쓴다.
// id 는 서버 기본값(api/services/tm/contract-field-config.js)과 **반드시 같아야** 한다.

export const UNITS = ['장', '개', '건', '회', '명', '박스', '세트'];

export const FIELD_SECTIONS = [
  {
    id: 'customer', name: '고객 정보', color: '#0ea5e9',
    fields: [
      { id: 'company_name', name: '상호',     kind: '한 줄 입력' },
      { id: 'phone',        name: '전화번호', kind: '한 줄 입력' },
      { id: 'mobile_phone', name: '휴대폰',   kind: '한 줄 입력' },
      { id: 'address',      name: '주소',     kind: '한 줄 입력' },
      { id: 'memo',         name: '비고',     kind: '여러 줄' },
    ],
  },
  {
    id: 'delivery', name: '배송 정보', color: '#0369a1',
    fields: [
      // 나중에 채우는 칸이라 필수로 만들 수 없다 (noReq)
      { id: 'courier',         name: '택배사',   kind: '한 줄 입력', noReq: true },
      { id: 'tracking_number', name: '송장번호', kind: '한 줄 입력', noReq: true },
    ],
  },
  {
    id: 'personnel', name: '담당자', color: '#8b5cf6',
    fields: [
      { id: 'user_name',         name: '상담자',        kind: '고르기' },
      { id: 'sales_person_name', name: '마케팅 담당자', kind: '고르기' },
    ],
  },
  {
    id: 'contract', name: '계약 정보', color: '#16a34a',
    fields: [
      { id: 'registrationType', name: '신규 / 재주문', kind: '버튼 두 개', noReq: true },
      // 계약 상태는 계약을 수정할 때만 나온다(신규는 언제나 계약완료) — 설정으로 바꿀 수 없다
      { id: 'contract_status',  name: '계약 상태',     kind: '버튼 두 개', noReq: true, editOnlyNote: true },
      { id: 'contract_date',    name: '계약일자',      kind: '날짜' },
      { id: 'contractProducts', name: '계약 상품',     kind: '상품 고르기', hasUnit: true, hasValidity: true },
      { id: 'serviceProducts',  name: '서비스 상품',   kind: '상품 고르기', noReq: true, hasUnit: true, hasValidity: true },
    ],
  },
  {
    id: 'promo', name: '홍보물', color: '#7c3aed', needsModule: true,
    fields: [
      { id: 'promoItems', name: '홍보물 품목', kind: '검색해서 담기', noReq: true, hasUnit: true },
    ],
  },
];

/**
 * 설정에 저장된 차례대로 섹션 id 를 돌려준다.
 * 설정이 없거나 order 가 없으면 이 파일에 적힌 기본 순서를 쓴다.
 */
export function orderedSectionIds(config) {
  const ids = FIELD_SECTIONS.map((s) => s.id);
  if (!config?.sections) return ids;
  return [...ids].sort((a, b) => {
    const oa = Number(config.sections[a]?.order);
    const ob = Number(config.sections[b]?.order);
    const na = Number.isFinite(oa) ? oa : ids.indexOf(a);
    const nb = Number.isFinite(ob) ? ob : ids.indexOf(b);
    if (na !== nb) return na - nb;
    return ids.indexOf(a) - ids.indexOf(b);   // 같은 값이면 기본 순서로
  });
}

/** 이 칸이 지금 모드(신규/재주문/수정)에서 보이는지. 설정이 기존 규칙보다 우선이다. */
export function isFieldVisible(config, sectionId, fieldId, { isNew }) {
  const sec = config?.sections?.[sectionId];
  const fld = config?.fields?.[fieldId];
  if (!sec?.on || !fld?.on) return false;
  // editOnly = 계약을 수정할 때만 나오는 칸(계약 상태). 설정으로 바꿀 수 없고 코드에 고정이다.
  if (isNew && (sec.editOnly || fld.editOnly)) return false;
  return true;
}

/** 섹션 자체가 보이는지 (안에 보이는 칸이 하나도 없으면 숨긴다) */
export function isSectionVisible(config, sectionId, { isNew }) {
  const sec = config?.sections?.[sectionId];
  if (!sec?.on) return false;
  if (isNew && sec.editOnly) return false;
  const meta = FIELD_SECTIONS.find((s) => s.id === sectionId);
  if (!meta) return false;
  return meta.fields.some((f) => isFieldVisible(config, sectionId, f.id, { isNew }));
}

/** 수량 단위 (설정이 없으면 '장') */
export function unitOf(config, fieldId) {
  return config?.fields?.[fieldId]?.unit || '장';
}

/** 유효기간(개월) 칸을 보일지 */
export function showValidity(config, fieldId) {
  return config?.fields?.[fieldId]?.validity !== false;
}

/** 이 칸이 필수인지 */
export function isRequired(config, fieldId) {
  return config?.fields?.[fieldId]?.req === true;
}
